/** Watching somebody else drive.
 *
 * This is the only screen in the portal that is not a request and a table. It
 * holds a WebSocket open, takes about four frames a second off it, and spends
 * the rest of its time drawing the gaps between them: the car is interpolated
 * along the circuit at the screen's own rate and the lap clock ticks ten
 * times a second, so a stream that is genuinely arriving at 4 Hz does not
 * look like one.
 *
 * The other half of the job is refusing to pretend. A spectate page has four
 * ways of being wrong — the driver has stopped, the stream is private, the
 * server is not carrying live data at all, or the socket has simply dropped —
 * and all four look identical to a browser's `WebSocket`, which reports a
 * closed connection and nothing else. So each attempt starts by asking
 * `/api/live` which streams this visitor may watch (`connection.ts` explains
 * why the listing, and not the spectate path, is what answers that), and the
 * screen says what happened rather than leaving a motionless dot on a map
 * implying a driver who is sitting very still. Where the service genuinely
 * does not distinguish two of the four — an address with no stream on it may
 * be an idle driver or a private one — the screen names both instead of
 * choosing, because choosing would be a guess printed as a fact.
 *
 * Addresses are account ids, not logins: `/live/usr_…`, matching the spectate
 * URL `GET /api/me` hands out and the id `GET /v1/live/{user}` expects.
 */

import { Link, useParams } from 'react-router-dom';

import { LiveMap } from '@/components/live/LiveMap';
import { StreamList } from '@/components/live/StreamList';
import { StreamVisibility, useLiveVisibility } from '@/components/live/StreamVisibility';
import { TimingStrip } from '@/components/live/TimingStrip';
import { useCarState } from '@/components/live/useLiveClock';
import { useLiveListing, useLiveStream } from '@/components/live/useLiveStream';
import { EmptyState, ErrorNote, LoadingPanel, Page, PulseDot } from '@/components/ui';
import { count, hz as formatHz, plural } from '@/lib/format';
import { useMe } from '@/lib/session';
import { useTrackGeometry } from '@/lib/useTrackGeometry';

/** The ring buffer's default, used for the footer note until `hello` says
 *  what this server is actually keeping. */
const DEFAULT_RING_SECONDS = 30;

/** The word beside the pulsing dot, per state. */
const KICKER: Record<string, string> = {
  connecting: 'connecting',
  live: 'live',
  reconnecting: 'reconnecting',
  ended: 'ended',
  offline: 'off air',
  private: 'private',
  unavailable: 'no stream',
  disabled: 'off',
  error: 'no signal',
};

function StatusKicker({ status, stalled }: { status: string; stalled: boolean }) {
  const word = stalled && status === 'live' ? 'waiting' : (KICKER[status] ?? status);
  const lit = status === 'live' && !stalled;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        fontSize: 11,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: lit ? 'var(--color-accent-300)' : 'var(--color-neutral-500)',
      }}
    >
      <PulseDot
        size={7}
        glow={lit}
        pulse={lit}
        tone={lit ? 'accent' : status === 'live' ? 'warn' : 'neutral'}
      />
      {word}
    </span>
  );
}

function Spectate({ streamId }: { streamId: string }) {
  const { me } = useMe();
  // A driver may always watch their own stream and an admin may watch
  // anybody's, which is what lets the probe say "off air" rather than "off
  // air, or private" about an address it finds nothing at.
  const stream = useLiveStream(
    streamId,
    me ? { id: me.user.id, admin: me.user.role === 'admin' } : null,
  );
  const { streams } = useLiveListing();
  const geometry = useTrackGeometry(stream.officialId);
  const visibility = useLiveVisibility();
  const own = visibility !== null && visibility.userId === streamId;

  const watchable = stream.status === 'live' || stream.status === 'reconnecting';
  const car = useCarState(stream.samples, watchable);
  const listed = streams.find((row) => row.user_id === streamId);

  const driver = listed?.display_name || (me?.user.id === streamId ? me.user.display_name : '');
  const track = stream.trackName || listed?.track_name || stream.officialId;
  const carName = stream.car || listed?.car || '';
  const rate = stream.hz ?? listed?.hz ?? stream.hello?.hz ?? null;
  const watching = listed?.spectators ?? stream.hello?.spectators ?? null;
  const ring = stream.hello?.ring_seconds || DEFAULT_RING_SECONDS;
  const url = `${window.location.host}/live/${streamId}`;

  const header = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
      <StatusKicker status={stream.status} stalled={car?.stalled ?? false} />
      <h1 style={{ fontSize: 20, margin: 0 }}>
        {driver || streamId}
        {track ? (
          <>
            <span style={{ color: 'var(--color-neutral-600)', fontWeight: 400 }}> · </span>
            {track}
          </>
        ) : null}
      </h1>
      {carName ? (
        <span style={{ fontSize: 12.5, color: 'var(--color-neutral-500)' }}>{carName}</span>
      ) : null}
      <span
        style={{
          marginLeft: 'auto',
          fontSize: 11.5,
          color: 'var(--color-neutral-600)',
          fontVariantNumeric: 'tabular-nums',
          display: 'flex',
          gap: 14,
          flexWrap: 'wrap',
        }}
      >
        {rate !== null ? <span>{formatHz(rate)}</span> : null}
        {watching !== null ? <span>{plural(watching, 'watching', 'watching')}</span> : null}
        {/* Your own stream carries the switch, read from the account rather
            than the listing. Anyone else's is only claimed public when the
            listing actually said so: a stream this visitor can see but the
            listing does not carry is one of their own, and guessing "public"
            about it would be a lie. */}
        {own && visibility ? (
          <StreamVisibility visibility={visibility} address={url} compact />
        ) : listed ? (
          <span>{listed.public ? 'public · no login' : 'private'}</span>
        ) : null}
      </span>
    </div>
  );

  const body = () => {
    const nothingToDraw = stream.frames === 0 && !stream.hello;
    if (stream.status === 'connecting' && nothingToDraw) {
      return <LoadingPanel label="connecting to the stream" />;
    }
    if (stream.status === 'reconnecting' && nothingToDraw) {
      return <LoadingPanel label="reconnecting to the stream" />;
    }

    if (stream.status === 'private') {
      return (
        <EmptyState
          title="this stream is private"
          action={
            me ? (
              <Link className="btn btn-secondary" to="/live">
                See who is streaming
              </Link>
            ) : (
              <Link className="btn btn-primary" to={`/signin?redirect_to=/live/${streamId}`}>
                Sign in
              </Link>
            )
          }
        >
          {stream.reason || 'the driver has not made this stream public'}. A driver turns spectating
          on from their profile; overlays and race control use a <code>live:read</code> token
          instead.
        </EmptyState>
      );
    }

    if (stream.status === 'unavailable') {
      return (
        <EmptyState
          title="nothing to watch there"
          action={
            stream.givenUp ? (
              <button type="button" className="btn btn-secondary" onClick={stream.retry}>
                Check again
              </button>
            ) : me ? (
              <Link className="btn btn-secondary" to="/live">
                See who is streaming
              </Link>
            ) : (
              <Link className="btn btn-primary" to={`/signin?redirect_to=/live/${streamId}`}>
                Sign in
              </Link>
            )
          }
        >
          {stream.reason || 'nobody is streaming at that address'}. The service does not say which
          of the two it is, so neither does this page. A driver turns spectating on from their
          profile; overlays and race control use a <code>live:read</code> token instead.{' '}
          {stream.givenUp
            ? 'This page has stopped checking.'
            : 'This page keeps checking, and the map appears the moment they go out.'}
        </EmptyState>
      );
    }

    if (stream.status === 'disabled') {
      return (
        <EmptyState title="live streaming is switched off">
          {stream.reason || 'the server is not accepting live right now'}. Track surveys and stored
          sessions are unaffected.
        </EmptyState>
      );
    }

    if (stream.status === 'ended') {
      return (
        <EmptyState
          title="the stream has ended"
          action={
            <Link className="btn btn-secondary" to="/live">
              See who else is streaming
            </Link>
          }
        >
          {stream.reason || 'the driver disconnected'}. Frames are not kept once a stream is over
          unless the driver opted into recording.
        </EmptyState>
      );
    }

    if (stream.status === 'offline') {
      return (
        <EmptyState
          title="nobody is driving there right now"
          action={
            stream.givenUp ? (
              <button type="button" className="btn btn-secondary" onClick={stream.retry}>
                Check again
              </button>
            ) : null
          }
        >
          {stream.reason || 'that driver is not streaming'}.{' '}
          {stream.givenUp
            ? 'This page has stopped checking.'
            : 'This page keeps checking, and the map appears the moment they go out.'}
        </EmptyState>
      );
    }

    if (stream.status === 'error') {
      return (
        <ErrorNote
          error={new Error(stream.reason || 'the stream could not be reached')}
          title="could not reach the stream"
          onRetry={stream.retry}
        />
      );
    }

    return (
      <>
        <LiveMap
          geometry={geometry}
          samples={stream.samples}
          car={car}
          marks={stream.timing.marks}
          startLine={stream.timing.startLine}
          running={stream.status === 'live'}
          trackName={track}
        />
        <TimingStrip timing={stream.timing} car={car} />
      </>
    );
  };

  return (
    <Page
      max={1200}
      label="Live spectate"
      style={{ padding: '18px 20px 32px', display: 'flex', flexDirection: 'column', gap: 14 }}
    >
      {header}

      <div aria-live="polite" className="sr-only">
        {stream.status === 'reconnecting'
          ? `connection lost, retrying — attempt ${stream.attempt}`
          : `stream ${stream.status}`}
      </div>

      {body()}

      {stream.status === 'reconnecting' ? (
        <div style={{ fontSize: 11.5, color: 'var(--color-warn)' }}>
          The connection dropped. Reconnecting — attempt {count(stream.attempt)}.
        </div>
      ) : null}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          fontSize: 11,
          color: 'var(--color-neutral-600)',
          flexWrap: 'wrap',
        }}
      >
        <span>
          Frames arrive every 250 ms, downsampled from the logger&rsquo;s 60 Hz feed. Only the last{' '}
          {count(ring)} s are kept unless the driver opted into recording.
        </span>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{url}</span>
      </div>
    </Page>
  );
}

export default function Live() {
  const { username } = useParams<{ username?: string }>();
  return username ? <Spectate streamId={username} /> : <StreamList />;
}
